



import React from 'react';
import PropTypes from 'prop-types';
import { styled, ThemeProvider, createTheme } from '@mui/material/styles';
import {
  Box,
  Card,
  Typography,
  Grid,
  Avatar,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  DirectionsCar,
  Schedule,
  Group,
  LocationOn,
  AttachMoney,
  CheckCircle,
  Share,
  PersonAdd,
  LocationOff,
} from '@mui/icons-material';

// Styled Components
const DashboardCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  borderRadius: 16,
  padding: theme.spacing(3),
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
  },
}));

const StatBox = styled(Box)(({ theme, bgcolor }) => ({
  background: `linear-gradient(135deg, ${bgcolor} 0%, ${bgcolor}dd 100%)`,
  borderRadius: 12,
  padding: theme.spacing(2),
  color: '#fff',
  position: 'relative',
  overflow: 'hidden',
  minHeight: 140,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '30%',
    height: '100%',
    background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.1))',
    transform: 'skewX(-15deg)',
  },
}));

const IconBox = styled(Avatar)(({ theme }) => ({
  backgroundColor: 'rgba(255,255,255,0.2)',
  width: 48,
  height: 48,
  borderRadius: 12,
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  borderRadius: 3,
  backgroundColor: 'rgba(255,255,255,0.2)',
  '& .MuiLinearProgress-bar': {
    borderRadius: 3,
  },
}));

// Theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#3a0ca3',
    },
    secondary: {
      main: '#f72585',
    },
  },
});

export const TripRevenueTab = ({
  currentTab,
  dailyAcceptedTripRequestsCount,
  dailyPendingTripRequestsCount,
  dailySharedTripsCount,
  dailyTripsCount,
  dailyNewPassengersCount,
  dailyRevenue,
  weeklyRevenue,
  monthlyRevenue,
  yearlyRevenue,
  trendingPickupLocations,
  trendingDestinationLocations,
}) => {
  const stats = [
    {
      title: 'Active Trips',
      value: dailyAcceptedTripRequestsCount,
      icon: <DirectionsCar />,
      color: '#4361ee',
      progress: 75,
    },
    {
      title: 'Pending Requests',
      value: dailyPendingTripRequestsCount,
      icon: <Schedule />,
      color: '#f72585',
      progress: 45,
    },
    {
      title: 'Completed Trips',
      value: dailyTripsCount,
      icon: <CheckCircle />,
      color: '#4cc9f0',
      progress: 90,
    },
    {
      title: 'Shared Rides',
      value: dailySharedTripsCount,
      icon: <Share />,
      color: '#7209b7',
      progress: 60,
    },
  ];

  const revenue = [
    {
      period: 'Daily',
      value: dailyRevenue,
      icon: <AttachMoney />,
    },
    {
      period: 'Weekly',
      value: weeklyRevenue,
      icon: <AttachMoney />,
    },
    {
      period: 'Monthly',
      value: monthlyRevenue,
      icon: <AttachMoney />,
    },
    {
      period: 'Yearly',
      value: yearlyRevenue,
      icon: <AttachMoney />,
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3, background: '#f8f9fa' }}>
        {/* Stats Section */}
        <Grid container spacing={3} mb={4}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <StatBox bgcolor={stat.color}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <IconBox>{stat.icon}</IconBox>
                  <Typography variant="h4" fontWeight="bold">
                    {stat.value}
                  </Typography>
                </Box>
                <Box mt={2}>
                  <Typography variant="subtitle2" sx={{ opacity: 0.7 }}>
                    {stat.title}
                  </Typography>
                  <ProgressBar variant="determinate" value={stat.progress} sx={{ mt: 1 }} />
                </Box>
              </StatBox>
            </Grid>
          ))}
        </Grid>

        {/* New Registrations */}
        <DashboardCard sx={{ mb: 4 }}>
          <Typography variant="h6" mb={3}>
            Daily Registrations
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <StatBox bgcolor="#f72585">
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <IconBox>
                    <PersonAdd />
                  </IconBox>
                  <Typography variant="h4" fontWeight="bold">
                    {dailyNewPassengersCount}
                  </Typography>
                </Box>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  New Passengers Today
                </Typography>
              </StatBox>
            </Grid>
          </Grid>
        </DashboardCard>

        {/* Revenue Section */}
        <DashboardCard sx={{ mb: 4 }}>
          <Typography variant="h6" mb={3}>
            Revenue Overview
          </Typography>
          <Grid container spacing={3}>
            {revenue.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid rgba(0,0,0,0.1)',
                    borderRadius: 2,
                    textAlign: 'center',
                  }}
                >
                  <Typography color="text.secondary">{item.period} Revenue</Typography>
                  <Typography variant="h5" color="primary.main" fontWeight="bold" mt={1}>
                    ${item.value?.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DashboardCard>

        {/* Locations Section */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <DashboardCard>
              <Box display="flex" alignItems="center" mb={2}>
                <LocationOn color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Popular Pickup Locations
                </Typography>
                </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                }}
              >
                <Box textAlign="center">
                  <Typography variant="h3" color="primary.main" fontWeight="bold">
                    {trendingPickupLocations}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" mt={1}>
                    Active Pickup Points
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={75}
                    sx={{ mt: 2, height: 8, borderRadius: 4 }}
                  />
                </Box>
              </Box>
            </DashboardCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <DashboardCard>
              <Box display="flex" alignItems="center" mb={2}>
                <LocationOff color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Trending Destinations
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                }}
              >
                <Box textAlign="center">
                  <Typography variant="h3" color="secondary.main" fontWeight="bold">
                    {trendingDestinationLocations}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" mt={1}>
                    Popular Destinations
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={85}
                    color="secondary"
                    sx={{ mt: 2, height: 8, borderRadius: 4 }}
                  />
                </Box>
              </Box>
            </DashboardCard>
          </Grid>
        </Grid>

        {/* Summary Cards */}
        <Box mt={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <DashboardCard>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6">Trip Summary</Typography>
                    <DirectionsCar color="primary" />
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ mt: 2 }}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="text.secondary">Total Trips</Typography>
                      <Typography fontWeight="bold">{dailyTripsCount}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="text.secondary">Shared Rides</Typography>
                      <Typography fontWeight="bold">{dailySharedTripsCount}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography color="text.secondary">Success Rate</Typography>
                      <Typography fontWeight="bold" color="success.main">
                        {((dailyTripsCount / (dailyTripsCount + dailyPendingTripRequestsCount)) * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </DashboardCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <DashboardCard>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6">Revenue Metrics</Typography>
                    <AttachMoney color="primary" />
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ mt: 2 }}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="text.secondary">Daily Average</Typography>
                      <Typography fontWeight="bold">${(dailyRevenue / dailyTripsCount).toFixed(2)}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="text.secondary">Monthly Projection</Typography>
                      <Typography fontWeight="bold">${(monthlyRevenue * 1.1).toFixed(2)}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography color="text.secondary">Growth Rate</Typography>
                      <Typography fontWeight="bold" color="success.main">+12.5%</Typography>
                    </Box>
                  </Box>
                </Box>
              </DashboardCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <DashboardCard>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6">User Activity</Typography>
                    <Group color="primary" />
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ mt: 2 }}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="text.secondary">New Users</Typography>
                      <Typography fontWeight="bold">{dailyNewPassengersCount}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="text.secondary">Active Trips</Typography>
                      <Typography fontWeight="bold">{dailyAcceptedTripRequestsCount}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography color="text.secondary">Engagement Rate</Typography>
                      <Typography fontWeight="bold" color="success.main">
                        {((dailyAcceptedTripRequestsCount / dailyNewPassengersCount) * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </DashboardCard>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

TripRevenueTab.propTypes = {
  currentTab: PropTypes.number.isRequired,
  dailyAcceptedTripRequestsCount: PropTypes.number,
  dailyPendingTripRequestsCount: PropTypes.number,
  dailySharedTripsCount: PropTypes.number,
  dailyTripsCount: PropTypes.number,
  dailyNewPassengersCount: PropTypes.number,
  dailyRevenue: PropTypes.number,
  weeklyRevenue: PropTypes.number,
  monthlyRevenue: PropTypes.number,
  yearlyRevenue: PropTypes.number,
  trendingPickupLocations: PropTypes.number,
  trendingDestinationLocations: PropTypes.number,
};

TripRevenueTab.defaultProps = {
    dailyAcceptedTripRequestsCount: 0,
    dailyPendingTripRequestsCount: 0,
    dailySharedTripsCount: 0,
    dailyTripsCount: 0,
    dailyNewPassengersCount: 0,
    dailyRevenue: 0,
    weeklyRevenue: 0,
    monthlyRevenue: 0,
    yearlyRevenue: 0,
    trendingPickupLocations: 0,
    trendingDestinationLocations: 0,
  };
  
  // Optional: Add custom styles for responsive design
  const styles = {
    container: {
      '@media (max-width: 600px)': {
        padding: '16px',
      },
    },
    card: {
      '@media (max-width: 600px)': {
        padding: '16px',
      },
    },
  };
  
  // Optional: Add animations
  const fadeInAnimation = {
    '@keyframes fadeIn': {
      from: {
        opacity: 0,
        transform: 'translateY(20px)',
      },
      to: {
        opacity: 1,
        transform: 'translateY(0)',
      },
    },
  };
  
  // Optional: Add custom error boundary
  class TripRevenueTabErrorBoundary extends React.Component {
    constructor(props) {
      super(props);
      this.state = { hasError: false };
    }
  
    static getDerivedStateFromError(error) {
      return { hasError: true };
    }
  
    componentDidCatch(error, errorInfo) {
      console.error('TripRevenueTab Error:', error, errorInfo);
    }
  
    render() {
      if (this.state.hasError) {
        return (
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              color: 'error.main',
            }}
          >
            <Typography variant="h6">
              Something went wrong loading the dashboard.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => window.location.reload()}
              sx={{ mt: 2 }}
            >
              Reload Page
            </Button>
          </Box>
        );
      }
  
      return this.props.children;
    }
  }
  
  // Export wrapped component with error boundary
  export default function TripRevenueTabWithErrorBoundary(props) {
    return (
      <TripRevenueTabErrorBoundary>
        <TripRevenueTab {...props} />
      </TripRevenueTabErrorBoundary>
    );
  }
  
  // Optional: Add loading state component
  export const TripRevenueTabSkeleton = () => {
    return (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={140}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };
  
  // Optional: Add component documentation
  /**
   * TripRevenueTab Component
   * 
   * A dashboard component that displays various metrics related to trips and revenue.
   * 
   * @component
   * @example
   * return (
   *   <TripRevenueTab
   *     currentTab={0}
   *     dailyAcceptedTripRequestsCount={10}
   *     dailyRevenue={1000}
   *     // ... other props
   *   />
   * )
   */
  
  // Optional: Add test file (TripRevenueTab.test.js)
  /**
   * import { render, screen } from '@testing-library/react';
   * import { TripRevenueTab } from './TripRevenueTab';
   * 
   * describe('TripRevenueTab', () => {
   *   it('renders without crashing', () => {
   *     render(<TripRevenueTab currentTab={0} />);
   *     expect(screen.getByText('Trip Summary')).toBeInTheDocument();
   *   });
   * 
   *   it('displays correct revenue values', () => {
   *     render(<TripRevenueTab currentTab={0} dailyRevenue={1000} />);
   *     expect(screen.getByText('$1000.00')).toBeInTheDocument();
   *   });
   * });
   */
  
  // Optional: Add style constants
  const COLORS = {
    primary: '#3a0ca3',
    secondary: '#f72585',
    success: '#4cc9f0',
    warning: '#f77f00',
    info: '#4361ee',
    error: '#ef233c',
  };
  
  const TRANSITIONS = {
    default: 'all 0.3s ease-in-out',
    fast: 'all 0.15s ease-in-out',
    slow: 'all 0.5s ease-in-out',
  };
  
  // Optional: Add utility functions
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };
  
  const calculatePercentage = (value, total) => {
    if (total === 0) return 0;
    return ((value / total) * 100).toFixed(1);
  };
  
  // Optional: Add constants for breakpoints
  const BREAKPOINTS = {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  };
  
  // End of file